import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/api';
import Card from '../components/Card';
import { HiArrowLeft } from 'react-icons/hi2';
import toast from 'react-hot-toast';

const InputField = ({ label, type = 'text', name, value, onChange }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-pro-dark-text-secondary">{label}</label>
        <input 
            type={type} 
            name={name} 
            id={name} 
            value={value} 
            onChange={onChange} 
            className="mt-1 w-full p-2 bg-pro-dark-surface border border-pro-dark-border rounded-md focus:ring-pro-dark-accent focus:border-pro-dark-accent"
        />
    </div>
);

const ModifierOffrePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({});
    const [fileToUpload, setFileToUpload] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [preview, setPreview] = useState(null);
    const [isPreviewing, setIsPreviewing] = useState(false);

    useEffect(() => {
        const fetchOffre = async () => {
            setIsLoading(true);
            try {
                const response = await api.get(`/offres/${id}`);
                setFormData(response.data);
            } catch (error) {
                console.error("Erreur lors de la récupération de l'offre", error);
                navigate('/offres');
            } finally {
                setIsLoading(false);
            }
        };
        fetchOffre();
    }, [id, navigate]);

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseInt(value, 10) || 0 : value }));
    };

    const handleFileChangeForUpload = (event) => {
        setFileToUpload(event.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const submissionData = new FormData();
            submissionData.append('formData', JSON.stringify(formData));
            if (fileToUpload) {
                submissionData.append('attachment_file', fileToUpload);
            }
            await api.put(`/offres/${id}`, submissionData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Offre mise à jour avec succès !');
            navigate('/offres');
        } catch (error) {
            console.error("Erreur lors de la mise à jour:", error);
            toast.error(`Erreur: ${error.response?.data?.detail || 'Une erreur est survenue.'}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePreview = async () => {
        setIsPreviewing(true);
        try {
            const response = await api.post('/offres/summary', { text: JSON.stringify(formData) });
            setPreview(response.data);
        } catch (error) {
            toast.error("Erreur lors de la génération du résumé");
        } finally {
            setIsPreviewing(false);
        }
    };
    
    // CORRECTION : La liste complète des champs est utilisée ici
    const fields = [
        { label: 'Date Collaboration', name: 'dateCollaboration', type: 'date' }, { label: 'Compte', name: 'compte' },
        { label: 'Secteur', name: 'secteur' }, { label: 'Numéro Référence', name: 'numeroReference' },
        { label: 'Numéro BH', name: 'numeroBH' }, { label: 'Donneur D\'ordre', name: 'donneurDordre' },
        { label: 'TJM Annoncé', name: 'tjmAnnonce', type: 'number' }, { label: 'Niveau Exp', name: 'niveauExp' },
        { label: 'Solution', name: 'solution' }, { label: 'Statut', name: 'statut' },
    ];

    if (isLoading) {
        return <p className="text-center text-pro-dark-text-secondary">Chargement de l'offre...</p>;
    }

    return (
        <div>
            <Link to="/offres" className="flex items-center gap-2 text-sm text-pro-dark-text-secondary hover:text-pro-dark-accent mb-6">
                <HiArrowLeft />
                Retour à la liste des offres
            </Link>
            <h1 className="text-3xl font-bold text-pro-dark-text-primary mb-8">Modifier l'appel d'offre</h1>
            <Card>
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {fields.map(field => (
                        <InputField key={field.name} {...field} value={formData[field.name] || ''} onChange={handleInputChange} />
                    ))}
                    <div className="md:col-span-3">
                        <label htmlFor="detailDuBesoin" className="block text-sm font-medium text-pro-dark-text-secondary">Détail du Besoin</label>
                        <textarea id="detailDuBesoin" name="detailDuBesoin" rows="4" value={formData.detailDuBesoin || ''} onChange={handleInputChange} className="mt-1 w-full p-2 bg-pro-dark-surface border border-pro-dark-border rounded-md focus:ring-pro-dark-accent focus:border-pro-dark-accent"></textarea>
                    </div>
                    <div className="md:col-span-3">
                        <label htmlFor="commentaire" className="block text-sm font-medium text-pro-dark-text-secondary">Commentaire</label>
                        <textarea id="commentaire" name="commentaire" rows="4" value={formData.commentaire || ''} onChange={handleInputChange} className="mt-1 w-full p-2 bg-pro-dark-surface border border-pro-dark-border rounded-md focus:ring-pro-dark-accent focus:border-pro-dark-accent"></textarea>
                    </div>
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-pro-dark-text-secondary">Joindre un fichier (pour sauvegarde)</label>
                        <input
                            type="file"
                            onChange={handleFileChangeForUpload}
                            accept=".pdf,.docx"
                            className="mt-1 w-full text-sm text-pro-dark-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-pro-dark-surface file:text-white hover:file:bg-pro-dark-border"
                        />
                    </div>
                    <div className="md:col-span-3 text-right">
                        <div className="flex justify-end gap-4">
                            <button
                                type="button"
                                onClick={handlePreview}
                                disabled={isPreviewing}
                                className="px-6 py-2 bg-pro-dark-surface border border-pro-dark-border rounded-lg text-pro-dark-text-secondary hover:text-pro-dark-accent"
                            >
                                {isPreviewing ? 'Génération...' : 'Prévisualiser'}
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="px-6 py-2 bg-pro-dark-accent text-white rounded-lg hover:bg-pro-dark-accent-hover disabled:bg-gray-600"
                            >
                                {isSaving ? 'Mise à jour...' : 'Mettre à jour'}
                            </button>
                        </div>
                    </div>
                    {preview && (
                        <div className="md:col-span-3 p-4 bg-pro-dark-surface border border-pro-dark-border rounded-lg">
                            <h3 className="text-lg font-semibold mb-2">Résumé</h3>
                            <p className="text-sm whitespace-pre-wrap mb-2">{preview.summary}</p>
                            <button
                                type="button"
                                onClick={() => navigator.clipboard.writeText(preview.summary)}
                                className="text-sm text-pro-dark-accent mb-4"
                            >
                                Copier le résumé
                            </button>
                            <h3 className="text-lg font-semibold mb-2">Email</h3>
                            <p className="text-sm whitespace-pre-wrap mb-2">{preview.email_template}</p>
                            <button
                                type="button"
                                onClick={() => navigator.clipboard.writeText(preview.email_template)}
                                className="text-sm text-pro-dark-accent"
                            >
                                Copier l'e-mail
                            </button>
                        </div>
                    )}
                </form>
            </Card>
        </div>
    );
};

export default ModifierOffrePage;